<!-- eslint-disable max-lines -->
<template>
    <div class="EntityDetails">
        <!-- this may eventually be removed and implemented as tab level loader instead -->
        <PendingScreen v-show="loading" />
        <JsonSchemaCustomForm
            v-show="!loading"
            v-slot="{ fields }"
            v-model="value"
            class="EntityDetails__content"
            :controller="controller"
        >
            <ActionableAlert
                v-if="alertConfig"
                class="EntityDetails__alert"
                :title="alertConfig.title"
                :name="alertConfig.name"
                :type="alertConfig.type"
                :icon="alertConfig.icon"
                :action-title="alertConfig.actionTitle"
                @action="onAlertAction"
            />
            <UnsavedChangesModal
                v-if="showConfirmationModal"
                @input="onModalAction"
            />
            <PrimaryContactChangeModal
                v-if="showPrimaryContactChangeModal"
                :previous-email="entity.contactEmail"
                :new-email="formContactEmail"
                @input="onPrimaryContactChangeConfirm"
            />
            <ProducerChangesModal
                v-if="showProducerChangesModal"
                :first-active-producer="isFirstActiveProducerChange"
                @input="onProducerChangesConfirm"
            />
            <div class="EntityDetails__header">
                <div class="EntityDetails__headerDisplayNameBlock" @focusout="onDisplayNameBlur">
                    <NewButton
                        variant="flat"
                        color="neutral"
                        size="icon-sm"
                        class="EntityDetails__backButton"
                        aria-label="Back"
                        @click="onCloseButtonClick"
                    >
                        <FontAwesomeIcon :icon="faAngleLeft" />
                    </NewButton>
                    <JsonSchemaCustomFormElement
                        class="EntityDetailsDisplayNameForm"
                        :field="fields.displayName"
                        :disabled="!entity.active"
                        wrap-value-mode
                    />
                </div>
                <div class="EntityDetails__headerActions">
                    <EntityDetailsStatusBar
                        :updating-entity="updatingEntity"
                        :entity="entity"
                        :is-loading-submission-link="isLoadingSubmissionLink"
                        :is-collateral-enabled="isCollateralsFeaturesEnabled"
                        :submission-link-per-entity-id="submissionLinkPerEntityId"
                        :entity-exception="entityLevelException"
                        v-bind="statusBarUnhandledEvents"
                        @grant-exceptions="grantExceptions"
                        @remove-exceptions="removeExceptions"
                        @open-collateral-mapping-modal="openCollateralMappingModal"
                        @view-submission-history="openHistoricDocumentModal"
                    />
                </div>
            </div>
            <Tabs v-slot="{ activeTabId }" :selected-tab="currentTab" @change="onTabChange">
                <Tab :id="ENTITY_DETAILS_TABS.OVERVIEW" name="Entity Details">
                    <EntityDetailsPropertiesTab
                        :entity="entity"
                        :is-evident-user="isEvidentUser"
                        :collaterals="reactiveState.pendingCollaterals"
                        :custom-property-collaterals="customPropertyCollaterals"
                        :custom-properties-equipment="customPropertiesEquipment"
                        :fields="fields"
                        :updating-entity="updatingEntity"
                        :enum-labels="enumLabels"
                        :disable-save="!isFormValid"
                        :has-changes="hasChanges"
                        :loading="savingEntityForm"
                        :save-failed="saveFailed"
                        :entity-risk-profiles="sortedEntityCriteriaGroups"
                        :producer-rows="producerRows"
                        :producer-coverage-types="producerCoverageTypes"
                        :entity-address="formAddress"
                        :producer-requirements-loaded="requirementDetailsLoaded"
                        :producer-server-errors="producerServerErrors"
                        :entity-contact-email="formContactEmail"
                        @collaterals-change="onCollateralsChange"
                        @equipment-change="onEquipmentChange"
                        @producers-change="onProducersChange"
                        @editing-change="isPropertiesTabEditing = $event"
                        @save="onSave"
                        @cancel="onCancelEdit"
                        @profile-click="(profile) => navigateToRiskProfile(profile.id)"
                    />
                </Tab>
                <Tab
                    :id="ENTITY_DETAILS_TABS.REQUIREMENT_TYPES"
                    class="EntityDetails__requirementTypesTab"
                    name="Compliance"
                >
                    <!-- Outside the empty state, so an open request shows even with no requirement cards. -->
                    <div class="EntityDetails__requirementList">
                        <OpenVerificationBanner
                            :open-verification="openVerification"
                            :requirement-models="requirementModels"
                            :credential-types="credentialTypes"
                            @force-submission="openForceSubmissionModal"
                        />
                        <Policy
                            v-for="requirementDetails in sortedDetailsList"
                            :key="getUniqueRequirementDetailsId(requirementDetails)"
                            :requirement-details="requirementDetails"
                            :all-requirements="sortedDetailsList"
                            :evaluation-errors="getEvaErrorMapForRequirement(requirementDetails)"
                            :entity="entity"
                            :entity-risk-profiles="entityRiskProfiles"
                            :requirement-models="requirementModels"
                            :credential-types="credentialTypes"
                            :collateral-entities="collateralEntities"
                            @go-to-criterion="goToCriterion"
                            @grant-exceptions="grantExceptions($event, requirementDetails.coverage)"
                            @remove-exceptions="removeExceptions"
                        />
                    </div>
                    <EmptyRequirementTypes v-if="sortedDetailsList.length === 0" />
                </Tab>
                <Tab
                    v-if="isCollateralsFeaturesEnabled"
                    :id="ENTITY_DETAILS_TABS.COLLATERALS"
                    class="EntityDetails__collateralsTab"
                    name="Collaterals"
                >
                    <CollateralTab
                        :collaterals="collateralEntities"
                        :sorted-details-list="sortedDetailsList"
                        :entity="entity"
                        @grant-exceptions="grantExceptions($event)"
                        @remove-exceptions="removeExceptions"
                    />
                </Tab>
                <Tab :id="ENTITY_DETAILS_TABS.EVENTS" name="History">
                    <ActivityHistoryTab
                        :entity="entity"
                        :rp-name="rpName"
                        :active="activeTabId === ENTITY_DETAILS_TABS.EVENTS"
                        :selected-filters="historyFilters"
                        :active-verification-ids="activeVerificationIds"
                        @filter-change="onHistoryFilterChange"
                    />
                </Tab>
            </Tabs>
        </JsonSchemaCustomForm>
    </div>
</template>

<script lang="ts">
/* eslint-disable max-lines -- the Vue 3 migration and the producer-editing feature grew this
   view past the limit from opposite ends; the producer logic already lives in
   utils/producer-forms and utils/entity-form, so what remains is component wiring. */
    import { PropType } from 'vue';

    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { isEmpty, isEqual, noop, omit, omitBy, sortBy } from 'lodash';
    import cloneDeep from 'lodash/cloneDeep';

    import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
    import { faAngleLeft } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import { Badge } from '@evidentid/dashboard-commons/components/Badge';
    import {
        JsonSchemaCustomForm,
        JsonSchemaCustomFormElement,
    } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import { Tab, Tabs } from '@evidentid/dashboard-commons/components/Tabs';
    import { isEvidentUser } from '@evidentid/dashboard-commons/modules/auth/getAuthRedirection';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import { PendingScreen } from '@evidentid/dashboard-commons/screens/PendingScreen';
    import createForm from '@evidentid/json-schema/createForm';
    import { FormController, Pointer } from '@evidentid/json-schema/FormController';
    import JsonSchema, { JsonSchemaObject } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { PatchOperationType } from '@evidentid/rpweb-api-client';
    import { OperationError, UpsertEntitiesFailure } from '@evidentid/rpweb-api-client/models';
    import { ActivityHistoryQuery } from '@evidentid/tprm-portal-lib/models/activity-history';
    import { TprmCredentialType } from '@evidentid/tprm-portal-lib/models/common';
    import { CustomProperty, Entity, EntityAddress } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        CustomPropertyCollateral,
        CustomPropertyEquipment,
        CustomPropertyValues,
    } from '@evidentid/tprm-portal-lib/models/dashboard/CustomProperty.model';
    import { EntityRiskProfile } from '@evidentid/tprm-portal-lib/models/decisioning';
    import {
        TprmRequirementType,
        ComplianceStatusOverride,
        ExceptionInput,
        ExceptionLevel,
        OpenVerification,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import {
        CollateralEntity,
    } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralEntity.model';
    import {
        EntityRequirement,
        EntityRequirementDetails,
    } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';
    import {
        TprmRequirementModel,
    } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementModel.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import ActionsButton from '@/modules/dashboard/components/ActionsButton/ActionsButton.vue';
    import ComplianceStatusBadge from '@/modules/dashboard/components/ComplianceStatusBadge/ComplianceStatusBadge.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { FieldEvaluationResultError } from '@/modules/decisioning-criteria/types';
    import { standardizeCriterionSchema } from '@/modules/decisioning-criteria/utils/standardizeCriterionSchema';
    import ActionableAlert from '@/modules/entity-details/components/ActionableAlert/ActionableAlert.vue';
    import { ActionableAlertConfig } from '@/modules/entity-details/components/ActionableAlert/types';
    import EffectiveRiskProfilesTooltipLabel
        from '@/modules/entity-details/components/EffectiveRiskProfilesTooltipLabel/EffectiveRiskProfilesTooltipLabel.vue';
    import EmptyRequirementTypes from '@/modules/entity-details/components/EmptyRequirementTypes/EmptyRequirementTypes.vue';
    import EntityDetailsStatusBar
        from '@/modules/entity-details/components/EntityDetailsStatusBar/EntityDetailsStatusBar.vue';
    import EntityRiskProfiles
        from '@/modules/entity-details/components/EntityRiskProfiles/EntityRiskProfiles.vue';
    import { ForceSubmissionRequest } from '@/modules/entity-details/components/OpenVerificationBanner/helpers';
    import OpenVerificationBanner
        from '@/modules/entity-details/components/OpenVerificationBanner/OpenVerificationBanner.vue';
    import Policy from '@/modules/entity-details/components/Policy/Policy.vue';
    import PrimaryContactChangeModal
        from '@/modules/entity-details/components/PrimaryContactChangeModal/PrimaryContactChangeModal.vue';
    import ProducerChangesModal
        from '@/modules/entity-details/components/ProducerChangesModal/ProducerChangesModal.vue';
    import UnsavedChangesModal from '@/modules/entity-details/components/UnsavedChangesModal/UnsavedChangesModal.vue';
    import { ENTITY_DETAILS_TABS } from '@/modules/entity-details/constants/entityDetailsTabs.constants';
    import { SUBMISSION_HISTORY_ACTIVITY_TYPES } from '@/modules/entity-details/constants/submissionHistoryActivityTypes.constants';
    import { ValidateProducerEmails } from '@/modules/entity-details/models/ValidateProducerEmails.model';
    import { useEntityDetailsStore } from '@/modules/entity-details/store';
    import {
        EntityDetailsValue,
        EntityFormProfile,
        buildEntityFormValue,
        buildEntityToBeSaved,
        normalizeEntityForDirtyCheck,
    } from '@/modules/entity-details/utils/entity-form/entityToBeSaved';
    import {
        filterOutIsPresentUsedFields,
    } from '@/modules/entity-details/utils/filterOutIsPresentUsedFields/filterOutIsPresentUsedFields';
    import {
        getEvaluationErrorsMapKey,
    } from '@/modules/entity-details/utils/getEvaluationErrorsMapKey/getEvaluationErrorsMapKey';
    import { mapEvaluationErrors } from '@/modules/entity-details/utils/mapEvaluationErrors';
    import {
        ProducerRow,
        ProducerRowErrors,
        getProducerEmails,
        haveProducersChanged,
        isFirstActiveProducerTransition,
        toProducerRows,
    } from '@/modules/entity-details/utils/producer-forms/producerRows';
    import {
        mapBlockedProducerEmails,
        mapProducerValidationDetails,
    } from '@/modules/entity-details/utils/producer-forms/producerValidationDetails';
    import ActivityHistoryTab
        from '@/modules/entity-events/components/ActivityHistoryTab/ActivityHistoryTab.vue';
    import { ActivityHistoryFilters } from '@/modules/entity-events/models/ActivityHistoryFilters.model';
    import { useEntityEventsStore } from '@/modules/entity-events/store';
    import {
        DUPLICATE_CONTACT_EMAIL_MESSAGE,
    } from '@/modules/entity-management/constants/entityMessages.constant';
    import {
        BatchUpsertEntitiesStatus,
        createEmptyUpsertEntitiesStatus,
        useEntityManagementStore,
    } from '@/modules/entity-management/store';
    import { buildEntityDetailsJsonSchema } from '@/modules/entity-management/utils/buildEntityDetailsJsonSchema';
    import { isCollateralCustomProperty } from '@/utils/isCollateralCustomProperty';
    import { isEquipmentCustomProperty } from '@/utils/isEquipmentCustomProperty';

    import EntityDetailsPropertiesTab
        from '../EntityDetailsPropertiesTab/EntityDetailsPropertiesTab.vue';

    import CollateralTab from './components/CollateralTab/CollateralTab.vue';
    import EntityDetailsCollateralSection
        from './components/EntityDetailsCollateralSection/EntityDetailsCollateralSection.vue';

    function isSameEmail(one: string | null, other: string | null): boolean {
        return (one || '').trim().toLowerCase() === (other || '').trim().toLowerCase();
    }

    function getContactEmailSchema(schema: JsonSchema): JsonSchema {
        const body = (schema as JsonSchemaObject).properties.body as JsonSchemaObject;
        return (body.properties.profile as JsonSchemaObject).properties.contactEmail;
    }

    interface EntityDetailsControllerOptions {
        /** Adds the Entity UUID field, which only Evident's own staff see. */
        isEvidentUser?: boolean;
    }

    /** Whether a field and every field nested under it is valid.
     *
     * A parent's own `valid` flag is worked out before custom validators are attached to its
     * fields, so on a freshly built controller it can miss a failure its field already reports.
     * An optional group left empty is valid as a whole, so its fields are not checked.
     */
    function isFieldTreeValid(field: Pointer): boolean {
        if (!field.required && field.empty) {
            return true;
        }
        const children: Pointer[] = field.properties ? Object.values(field.properties) : field.items || [];
        return field.valid && children.every(isFieldTreeValid);
    }

    /** Builds the form controller, rejecting `takenContactEmail` on the Primary Contact Email field
     * so the API's duplicate-email refusal is shown under that input rather than as a page banner.
     *
     * `value` seeds the new controller. The inputs render from the controller, and swapping it does
     * not re-push the form's value, so leaving this out empties every field on screen.
     */
    function buildEntityDetailsController(
        customProperties: CustomProperty[],
        takenContactEmail: string | null = null,
        value: EntityDetailsValue | null = null,
        { isEvidentUser = false }: EntityDetailsControllerOptions = {},
    ): FormController {
        const schema = standardizeCriterionSchema(
            buildEntityDetailsJsonSchema(customProperties, undefined, undefined, isEvidentUser),
        );
        if (!takenContactEmail) {
            return new FormController(createForm(schema), value ?? undefined);
        }
        const contactEmailSchema = getContactEmailSchema(schema);
        // JsonSchemaEmailForm reads the message it shows off the schema. A field validator is
        // ANDed with the schema's own checks, so the format error still comes through.
        contactEmailSchema.strings = {
            ...contactEmailSchema.strings,
            'error.email.invalid': DUPLICATE_CONTACT_EMAIL_MESSAGE,
        };
        return new FormController(createForm(schema), value ?? undefined, {
            body: {
                profile: {
                    contactEmail: (_formValue: any, contactEmail?: string) =>
                        !isSameEmail(contactEmail ?? null, takenContactEmail),
                },
            },
        });
    }

    /** Drops the keys a row left blank. The row editors keep every optional field on the object,
     * so an untouched one would otherwise be sent as an empty value the API refuses. `false` and
     * `0` are answers rather than blanks, and stay. */
    function getRowsOmittingNullProps<T extends object>(rows?: T[]): T[] | undefined {
        if (!rows) {
            return rows;
        }
        const isNullishOrEmptyValue = (val: any): boolean => (
            typeof val === 'object' ? isEmpty(val) : val == null || val === ''
        );

        return rows.map((row) => omitBy(row, isNullishOrEmptyValue) as T);
    }

    /** Strips the blanks the Equipment row editors leave behind, for every key given. */
    function getCustomPropertiesOmittingEquipmentNullProps(
        customProperties: CustomPropertyValues | null,
        equipmentKeys: string[],
    ): CustomPropertyValues | null {
        if (!customProperties) {
            return customProperties;
        }

        return equipmentKeys.reduce((values, key) => (
            values[key]
                ? { ...values, [key]: getRowsOmittingNullProps(values[key] as CustomPropertyEquipment[]) }
                : values
        ), customProperties);
    }

    function getCustomPropertiesOmittingCollateralsNullProps(
        customProperties: CustomPropertyValues | null,
    ): CustomPropertyValues | null {
        if (!customProperties) {
            return customProperties;
        }

        if (customProperties.collaterals) {
            return {
                ...customProperties,
                collaterals: getRowsOmittingNullProps(
                    customProperties.collaterals as CustomPropertyCollateral[],
                ),
            };
        }

        return customProperties;
    }

    @Component({
        emits: [ 'close', 'evaluationUpdated', 'goToCriterion', 'rename-entity', 'updateEntities' ],
        components: {
            ActionableAlert, ActionsButton, ActivityHistoryTab, Alert, Badge, NewButton, ComplianceStatusBadge,
            EffectiveRiskProfilesTooltipLabel, EmptyRequirementTypes, FontAwesomeIcon, EntityRiskProfiles,
            EntityDetailsStatusBar, UnsavedChangesModal, JsonSchemaCustomForm, JsonSchemaCustomFormElement,
            PendingScreen, Policy, Tab, Tabs, EntityDetailsCollateralSection, CollateralTab,
            EntityDetailsPropertiesTab, PrimaryContactChangeModal, ProducerChangesModal, OpenVerificationBanner,
        },
    })
    export default class EntityDetails extends Vue {
        @Prop({ type: Object, required: true })
        private entity!: Entity;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: Array, default: () => [] })
        private requirementDetailsList!: EntityRequirementDetails[];

        /** Whether `requirementDetailsList` finished loading. A failed fetch also arrives
         * empty, and the producer card treats the two differently. */
        @Prop({ type: Boolean, default: true })
        private requirementDetailsLoaded!: boolean;

        @Prop({ type: Array, default: () => [] })
        private collateralEntities!: CollateralEntity[];

        @Prop({ type: Array, default: () => [] })
        private entityRiskProfiles!: EntityRiskProfile[];

        @Prop({ type: Array, default: () => [] })
        private currentlyUpdatedEntitiesIds!: string[];

        @Prop({ type: Boolean, default: false })
        private isLoadingSubmissionLink!: boolean;

        @Prop({ type: Boolean, default: false })
        private loading!: boolean;

        @Prop({ type: Object, default: () => ({}) })
        private submissionLinkPerEntityId!: Record<string, string>;

        @Prop({ type: Boolean, default: false })
        private isCollateralEnabled!: boolean;

        @Prop({ type: Array, default: () => [] })
        private requirementModels!: TprmRequirementModel[];

        @Prop({ type: Array, default: () => [] })
        private credentialTypes!: TprmCredentialType[];

        @Prop({ type: Object, default: () => ({}) })
        private externalAlertConfig!: ActionableAlertConfig;

        @Prop({ type: String as PropType<string>, default: '' })
        private collateralCustomPropertyKey!: string;

        @Prop({ type: Object as PropType<CategorizedEnumLabels>, default: (): CategorizedEnumLabels => ({}) })
        private enumLabels!: CategorizedEnumLabels;

        // Reports which producer emails use a blocked domain. Injected because this component owns
        // the save sequencing but holds no store access of its own.
        @Prop({ type: Function as PropType<ValidateProducerEmails>, default: async () => [] })
        private validateProducerEmails!: ValidateProducerEmails;

        private showConfirmationModal: boolean = false;
        private showPrimaryContactChangeModal: boolean = false;
        private showProducerChangesModal: boolean = false;
        private blockedProducerEmails: string[] = [];
        private isPropertiesTabEditing: boolean = false;
        private value: EntityDetailsValue | null = null;
        private controller: FormController = buildEntityDetailsController([]);
        private formValid: boolean = false;
        private unsubscribeFormValidity: (() => void) | null = null;
        private takenContactEmail: string | null = null;
        private faAngleLeft = faAngleLeft;
        private faCheckCircle = faCheckCircle;
        private faExclamationTriangle = faExclamationTriangle;
        private alertTimeout: any = null;
        private startedUpdatingEntity: boolean = false;
        private sortedDetailsList: EntityRequirementDetails[] = [];
        private sortedEntityCriteriaGroups: EntityRiskProfile[] = [];
        // Store in a reactive object to ensure Vue tracks changes
        private reactiveState = {
            pendingCollaterals: undefined as CustomPropertyCollateral[] | undefined,
            // Equipment rows per custom property key. An RP may configure several Equipment
            // properties, and each section reports its own rows. The entity form holds no Equipment
            // value of its own, so these rows are the only source the save payload has.
            pendingEquipment: {} as Record<string, CustomPropertyEquipment[]>,
            pendingProducers: undefined as ProducerRow[] | undefined,
        };

        private localAlertConfig: ActionableAlertConfig | null = null;

        // PRODUCT-26110 Phase 2B2: getter, not a field. A field initializer captures
        // `this.$listeners` at construction, before vue-facing-decorator wires up the
        // Options-API surface, so it would read `undefined` and forward no events. A
        // getter resolves `this.$listeners` at access (render) time.
        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): `$listeners` is gone in Vue 3 --
        // its keys now live in `$attrs` as `onXxx` (camelCased: `view-submission-history`
        // -> `onViewSubmissionHistory`).
        private get statusBarUnhandledEvents(): Record<string, unknown> {
            return omit(this.$attrs, [
                'onGrantExceptions', 'onRemoveExceptions', 'onOpenCollateralMappingModal',
                'onViewSubmissionHistory',
            ]);
        }

        private get customPropertiesOther(): CustomProperty[] {
            return this.customProperties.filter((customProperty) =>
                !isCollateralCustomProperty(customProperty) && !isEquipmentCustomProperty(customProperty),
            );
        }

        /** The Equipment properties, each of which is edited in a section of its own. They are kept
         * out of the entity form so that the generic Custom Properties card does not list them
         * alongside their sections. */
        private get customPropertiesEquipment(): CustomProperty[] {
            return this.customProperties.filter((customProperty) =>
                isEquipmentCustomProperty(customProperty),
            );
        }

        private get customPropertyCollaterals(): CustomProperty | null {
            return this.customProperties.find((customProperty) =>
                isCollateralCustomProperty(customProperty),
            ) || null;
        }

        private get collaterals(): CustomPropertyCollateral[] | undefined {
            const key = this.collateralCustomPropertyKey;
            return this.entity.insuredFields && key
                ? this.entity.insuredFields[key] as CustomPropertyCollateral[]
                : undefined;
        }

        /** The entity's stored rows for every Equipment property that has any. */
        private get equipment(): Record<string, CustomPropertyEquipment[]> {
            return this.customPropertiesEquipment.reduce((rowsByKey, customProperty) => {
                const rows = this.entity.insuredFields?.[customProperty.key] as CustomPropertyEquipment[] | undefined;
                return rows ? { ...rowsByKey, [customProperty.key]: [ ...rows ] } : rowsByKey;
            }, {} as Record<string, CustomPropertyEquipment[]>);
        }

        private get producerFlowEnabled(): boolean {
            return Boolean((this as any).$rp?.currentDetails?.producerFlowEnabled);
        }

        /** The contact email as the form holds it — the same form edits this field, so the
         * persisted value goes stale mid-session. */
        private get formContactEmail(): string {
            return (this.value?.body.profile.contactEmail ?? this.entity.contactEmail) || '';
        }

        /** The address as the form holds it. Like `formContactEmail`, the same form edits this
         * field, so the persisted value goes stale mid-session — and it decides whether workers
         * compensation may be given to a producer. */
        private get formAddress(): Partial<EntityAddress> | null {
            const profile = this.value?.body.profile as { address?: Partial<EntityAddress> | null } | undefined;
            if (profile && 'address' in profile) {
                return profile.address ?? null;
            }
            return this.entity.address ?? null;
        }

        private get producerRows(): ProducerRow[] {
            return this.reactiveState.pendingProducers || [];
        }

        /** The coverage types this entity has requirements for. Multi-instance requirements repeat
         * a coverage type, and a producer's `coverageTypes` holds plain enum keys, so dedupe. */
        private get producerCoverageTypes(): string[] {
            return [ ...new Set(this.requirementDetailsList.map((details) => details.coverageType)) ];
        }

        /** Producer field errors from the last refused save, keyed by row key. */
        private get producerServerErrors(): Record<string, ProducerRowErrors> {
            return {
                ...mapProducerValidationDetails(
                    this.entityUpdateFailures.flatMap((failure) => failure.error?.details || []),
                    this.producerRows,
                ),
                ...mapBlockedProducerEmails(this.blockedProducerEmails, this.producerRows),
            };
        }

        /** Whether the save hands the profile to a different person. Compared the way the service
         * compares it, so a re-cased address is not treated as a handover and does not prompt. */
        private get contactEmailChanged(): boolean {
            return !isSameEmail(this.formContactEmail, this.entity.contactEmail);
        }

        private get producersChanged(): boolean {
            return this.producerFlowEnabled
                && this.reactiveState.pendingProducers !== undefined
                && haveProducersChanged(this.producerRows, this.entity.producerList || []);
        }

        private get isFirstActiveProducerChange(): boolean {
            return isFirstActiveProducerTransition(this.entity.producerList || [], this.producerRows);
        }

        private get isFormValid(): boolean {
            return this.formValid;
        }

        // The controller updates its fields' `valid` flags without Vue seeing it, so a getter
        // reading them would stay stuck on the result from when the form loaded. Copying the
        // result into data on every controller change keeps it current.
        @Watch('controller', { immediate: true })
        private subscribeToFormValidity(): void {
            this.unsubscribeFormValidity?.();
            const updateFormValid = () => {
                this.formValid = isFieldTreeValid(this.fields.displayName) && isFieldTreeValid(this.fields.body);
            };
            updateFormValid();
            this.unsubscribeFormValidity = this.controller.onChange(updateFormValid);
        }

        private get isEvidentUser(): boolean {
            return isEvidentUser(useUserStore().data?.email);
        }

        private get hasChanges(): boolean {
            if (!this.value) {
                return false;
            }

            if (!isEqual(
                normalizeEntityForDirtyCheck(this.entity),
                normalizeEntityForDirtyCheck(this.entityToBeSaved),
            )) {
                return true;
            }
            return false;
        }

        private get fields(): Record<string, Pointer> {
            return this.controller.structure.properties!;
        }

        private get rpName(): string {
            return this.$rp.current!;
        }

        private readonly ENTITY_DETAILS_TABS = ENTITY_DETAILS_TABS;

        private get currentTab(): string {
            return (this.$route?.query?.tab as string | undefined) || ENTITY_DETAILS_TABS.REQUIREMENT_TYPES;
        }

        private get activeVerificationIds(): string[] {
            return this.requirementDetailsList
                .map((details) => details.coverage?.requestId)
                .filter((id): id is string => Boolean(id));
        }

        private get historyFilters(): ActivityHistoryFilters {
            const raw = this.$route.query.activityType as string | undefined;
            return {
                entryType: raw ? raw.split(',').filter(Boolean) : [],
                tags: [],
                causedBy: '',
            };
        }

        private onTabChange(tabId: string): void {
            this.$router.replace({ query: { ...this.$route.query, tab: tabId } }).catch(noop);
        }

        private onHistoryFilterChange(query: ActivityHistoryQuery): void {
            const activityType = query.entryType?.join(',') || undefined;
            this.$router.replace({
                query: { ...this.$route.query, activityType },
            }).catch(noop);
        }

        private get saveFailed(): boolean {
            return this.ownUpdateRequestFailed || this.entityUpdateFailures.length > 0;
        }

        // A failed request carries no elements to tell whose save it was, and the bulk import
        // shares this status, so only count it when this component started the save.
        private get ownUpdateRequestFailed(): boolean {
            return this.startedUpdatingEntity
                && this.upsertEntitiesStatus.status === OperationStatus.error;
        }

        private get updatingEntity(): boolean {
            return this.savingEntityForm || this.currentlyUpdatedEntitiesIds.includes(this.entity.id);
        }

        /**
         * Whether the form's own save is in flight, as opposed to any entity-level operation.
         *
         * The Properties tab leaves edit mode when this falls back to false, so it must not cover
         * work the form did not start -- a rename, a pause, a deactivation -- or that work would
         * close the tab mid-edit and take the unsaved fields with it.
         */
        private get savingEntityForm(): boolean {
            return this.upsertEntitiesStatus.status === OperationStatus.loading
                && this.startedUpdatingEntity;
        }

        private get isCollateralsFeaturesEnabled(): boolean {
            return this.isCollateralEnabled && this.collateralEntities.length > 0;
        }

        private get upsertEntitiesStatus(): BatchUpsertEntitiesStatus {
            return useEntityManagementStore().upsertEntitiesStatus[this.rpName]
                || createEmptyUpsertEntitiesStatus();
        }

        private get grantExceptionStatus(): OperationStatus {
            return useEntityDetailsStore().grantExceptionStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get removeExceptionStatus(): OperationStatus {
            return useEntityDetailsStore().removeExceptionStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get openVerification(): OpenVerification | null {
            const status = useEntityDetailsStore().openVerificationStatus[this.rpName];
            return status?.entityId === this.entity.id ? status.openVerification : null;
        }

        private get entityLevelException(): ComplianceStatusOverride | null {
            const details = this.requirementDetailsList.find(
                (details) => details.complianceStatusMetadata &&
                    details.complianceStatusMetadata.statusOverride &&
                    details.complianceStatusMetadata.statusOverride.level === ExceptionLevel.insured);
            return details?.complianceStatusMetadata?.statusOverride || null;
        }

        private get effectiveRiskProfileNames(): string[] {
            return this.sortedEntityCriteriaGroups
                .filter((group) => group.active)
                .map((group) => group.displayName);
        }

        private get alertConfig(): ActionableAlertConfig {
            return this.externalAlertConfig || this.localAlertConfig;
        }

        private get allRequirementTypeEvaluationErrors(): Record<string, Record<string, FieldEvaluationResultError>> {
            return this.sortedDetailsList.reduce((acc, requirementDetails) => {
                const key = getEvaluationErrorsMapKey(requirementDetails.coverageType, requirementDetails.instanceId);
                if (requirementDetails.coverage) {
                    acc[key] = mapEvaluationErrors(
                        requirementDetails.evaluationResults ?? [],
                        this.entityRiskProfiles,
                        requirementDetails,
                    );
                } else {
                    acc[key] = {};
                }
                return acc;
            }, {} as Record<string, Record<string, FieldEvaluationResultError>>);
        }

        private get entityToBeSaved(): Entity {
            const entityToBeSaved = buildEntityToBeSaved({
                entity: this.entity,
                profile: (this.value ? this.value.body.profile : {}) as EntityFormProfile,
                insuredFields: this.value?.body.insuredFields,
                pendingCollaterals: this.reactiveState.pendingCollaterals,
                collateralCustomPropertyKey: this.collateralCustomPropertyKey,
                pendingProducers: this.reactiveState.pendingProducers,
                producerFlowEnabled: this.producerFlowEnabled,
            });
            // Scoped to the properties that still exist: a property deleted from configuration
            // mid-session can leave rows behind in the pending map, and merging those would write
            // a property back onto the entity that its schema no longer has.
            const pendingEquipment = Object.fromEntries(
                this.customPropertiesEquipment
                    .map(({ key }) => [ key, this.reactiveState.pendingEquipment[key] ])
                    .filter(([ , rows ]) => rows !== undefined),
            );
            if (Object.keys(pendingEquipment).length === 0) {
                return entityToBeSaved;
            }
            return {
                ...entityToBeSaved,
                insuredFields: { ...entityToBeSaved.insuredFields, ...pendingEquipment },
            };
        }

        @Watch('entity', { immediate: true })
        private onEntityChange(): void {
            if (!this.value) {
                this.resetFormToEntity();
            } else if (!this.isPropertiesTabEditing) {
                // The Equipment sections read their rows from the entity, so a reload that brings
                // in different rows has to reach the pending ones too — otherwise the next save
                // writes back the rows loaded when the screen opened. Rows being edited are left
                // alone; the sections still hold them.
                this.reactiveState.pendingEquipment = this.equipment;
                // The profile fields are only editable from this card, so while it is closed a
                // reload can refresh them safely. Left stale, a later save writes back an address
                // the entity no longer has — and asks to hand the profile to one nobody typed.
                this.value = {
                    ...this.value,
                    body: {
                        ...this.value.body,
                        profile: buildEntityFormValue(this.entity, this.collateralCustomPropertyKey).body.profile,
                    },
                };
            }
        }

        @Watch('customPropertiesOther', { immediate: true })
        @Watch('takenContactEmail')
        @Watch('isEvidentUser')
        private onCustomPropertiesChange(): void {
            this.controller = buildEntityDetailsController(
                this.customPropertiesOther, this.takenContactEmail, this.value,
                { isEvidentUser: this.isEvidentUser });
        }

        // Editing the address clears the refusal, so the field goes back to reporting its own
        // format errors rather than the one the API sent about the previous value.
        @Watch('value.body.profile.contactEmail')
        private onContactEmailEdited(contactEmail: string): void {
            if (this.takenContactEmail && !isSameEmail(contactEmail, this.takenContactEmail)) {
                this.takenContactEmail = null;
            }
        }

        @Watch('upsertEntitiesStatus', { immediate: true })
        private onUpdateEntityChange(): void {
            this.showUpdateErrorIfEncountered();
            this.showUpdateSuccessIfEncountered();
            this.resetUpdatingEntityStatusIfDone();
        }

        @Watch('requirementDetailsList', { immediate: true })
        private onRequirementDetailsChange(): void {
            /*  currently we are only supporting credential types as multi instance requirements. however once we
             * support other types, we need to update this logic. hopefully we can have one single api to fetch
             * all instance models and use instanceId to find labels within that list rather than checking what
             * all categories we have and then find labels for them by calling separate apis per category.
             */
            const requirementDetailsListNew = cloneDeep(sortBy(this.requirementDetailsList, [
                (details) =>
                    this.requirementModels.find((model) => model.coverageType === details.coverageType)?.label ||
                    details.coverageType,
                (details) =>
                    this.credentialTypes.find(
                        (model) => model.id === details.instanceId,
                    )?.displayName || details.instanceId,
            ]));
            requirementDetailsListNew.forEach((details) => {
                details.evaluationResults =
                    details.evaluationResults
                        .map((evaluationResult) => filterOutIsPresentUsedFields(evaluationResult));
            });
            this.sortedDetailsList = requirementDetailsListNew;
        }

        @Watch('entityRiskProfiles', { immediate: true })
        private onEntityRiskProfilesChange(): void {
            this.sortedEntityCriteriaGroups =
                sortBy(this.entityRiskProfiles, [ (group) => group.displayName.toLowerCase() ]);
        }

        private onModalAction(confirmed: boolean): void {
            if (confirmed) {
                this.showConfirmationModal = false;
            } else {
                this.$emit('close');
            }
        }

        private onAlertAction(alertName: string) {
            if (alertName === 'undo') {
                this.undoDeletion();
            }
        }

        private onCloseButtonClick(): void {
            if ((this.isPropertiesTabEditing || this.isCollateralEditing) && this.hasChanges) {
                this.showConfirmationModal = true;
            } else {
                this.$emit('close');
            }
        }

        private onCollateralsChange(collaterals: CustomPropertyCollateral[]): void {
            this.reactiveState.pendingCollaterals = collaterals;
        }

        private onEquipmentChange(
            { key, rows }: { key: string, rows: CustomPropertyEquipment[] },
        ): void {
            this.reactiveState.pendingEquipment = { ...this.reactiveState.pendingEquipment, [key]: rows };
        }

        private onProducersChange(rows: ProducerRow[]): void {
            this.reactiveState.pendingProducers = rows;
            // Recheck on the next save rather than holding a verdict about an address that may
            // since have been corrected.
            this.blockedProducerEmails = [];
        }

        private resetFormToEntity(): void {
            this.value = buildEntityFormValue(this.entity, this.collateralCustomPropertyKey);
            this.reactiveState.pendingCollaterals = this.collaterals ? [ ...this.collaterals ] : undefined;
            this.reactiveState.pendingEquipment = this.equipment;
            this.reactiveState.pendingProducers = toProducerRows(this.entity.producerList || []);
        }

        private onCancelEdit(): void {
            this.resetFormToEntity();
        }

        private onDisplayNameBlur(): void {
            const newValue = this.value?.displayName;
            if (!this.fields.displayName.valid || !newValue || newValue === this.entity.displayName) {
                return;
            }
            this.$emit('rename-entity', {
                id: this.entity.id,
                displayName: { op: PatchOperationType.replace, newValue },
            });
        }

        /**
         * Handles a save attempt, which is not the same as a save.
         *
         * The producer email check runs unconditionally, before anything decides whether the save
         * may proceed. Its errors land on the same producer rows as the locally validated ones, so
         * gating it behind local validation would report the two in separate rounds — one save to
         * learn about the fields, another to learn about the domain. `localValidationPassed` gates
         * the save, not the check.
         */
        private async onSave(localValidationPassed: boolean): Promise<void> {
            if (!this.hasChanges || !this.value) {
                return;
            }

            if (this.producersChanged) {
                this.blockedProducerEmails =
                    await this.validateProducerEmails(getProducerEmails(this.producerRows));
            }

            if (!localValidationPassed || !this.isFormValid || this.blockedProducerEmails.length > 0) {
                return;
            }

            // Both of these reach people outside the portal, so they are confirmed before the save.
            // Handing the profile to a new contact is the heavier of the two and is asked first;
            // confirming it falls through to the producer prompt when that applies too.
            if (this.contactEmailChanged) {
                this.showPrimaryContactChangeModal = true;
                return;
            }

            if (this.producersChanged) {
                this.showProducerChangesModal = true;
                return;
            }

            this.saveEntity();
        }

        private onPrimaryContactChangeConfirm(confirmed: boolean): void {
            this.showPrimaryContactChangeModal = false;
            // Declining leaves the form in edit mode with the edits intact.
            if (!confirmed) {
                return;
            }
            if (this.producersChanged) {
                this.showProducerChangesModal = true;
                return;
            }
            this.saveEntity();
        }

        private onProducerChangesConfirm(confirmed: boolean): void {
            this.showProducerChangesModal = false;
            // Declining leaves the form in edit mode with the edits intact.
            if (confirmed) {
                this.saveEntity();
            }
        }

        private saveEntity(): void {
            this.updateEntity(this.entityToBeSaved);
            this.startedUpdatingEntity = true;
        }

        private updateEntity(entity: Entity) {
            const entityToEmit = entity;

            if (entityToEmit?.insuredFields?.collaterals) {
                entityToEmit.insuredFields = getCustomPropertiesOmittingCollateralsNullProps(
                    entityToEmit.insuredFields,
                );
            }
            if (entityToEmit?.insuredFields) {
                entityToEmit.insuredFields = getCustomPropertiesOmittingEquipmentNullProps(
                    entityToEmit.insuredFields,
                    this.customPropertiesEquipment.map((customProperty) => customProperty.key),
                );
            }
            this.$emit('updateEntities', [ entityToEmit ]);
        }

        // The contactEmail the API refused as already in use, or null. Drives the field-level
        // error; a duplicate is a validation failure on one property, not a page-level problem.
        private get duplicateContactEmailFailure(): UpsertEntitiesFailure | undefined {
            return this.entityUpdateFailures.find((x) => x.error?.error === OperationError.keyConflict);
        }

        // The bulk import shares this status, so filtering by id keeps a refused import row from
        // being reported as this entity's save.
        private get entityUpdateFailures(): UpsertEntitiesFailure[] {
            return this.upsertEntitiesStatus.failures.filter((x) => x.element?.id === this.entity.id);
        }

        private showUpdateErrorIfEncountered(): void {
            const duplicateFailure = this.duplicateContactEmailFailure;
            if (duplicateFailure) {
                this.takenContactEmail = duplicateFailure.element.contactEmail;
            }
            if (this.upsertEntitiesStatus.status === OperationStatus.success
                && this.entityUpdateFailures.length > 0) {
                if (duplicateFailure) {
                    // Reported under the Primary Contact Email input instead.
                    return;
                }
                if (Object.keys(this.producerServerErrors).length > 0) {
                    // Reported under the offending producer's field instead.
                    return;
                }
                this.setTimedAlert({
                    type: 'danger',
                    title: 'Cannot update the entity, an unknown error occurred.',
                });
            } else if (this.ownUpdateRequestFailed) {
                this.setTimedAlert({
                    type: 'danger',
                    title: 'Sorry, something went wrong during update entity. Please try again later.',
                });
            }
        }

        private showUpdateSuccessIfEncountered(): void {
            const isCurrentEntitySuccess =
                this.upsertEntitiesStatus.successes.some((x) => x.id === this.entity.id);
            if (
                this.startedUpdatingEntity &&
                this.upsertEntitiesStatus.status === OperationStatus.success &&
                this.entityUpdateFailures.length === 0 &&
                isCurrentEntitySuccess
            ) {
                this.setTimedAlert({
                    icon: this.faCheckCircle,
                    type: 'success',
                    title: 'Entity updated successfully.',
                });
            }
        }

        private resetUpdatingEntityStatusIfDone(): void {
            if (this.upsertEntitiesStatus.successes.some((x) => x.id === this.entity.id) ||
                this.entityUpdateFailures.length > 0
            ) {
                this.startedUpdatingEntity = false;
            }
        }

        private hideAlert(): void {
            this.localAlertConfig = null;
            clearTimeout(this.alertTimeout);
        }

        private setTimedAlert(config: ActionableAlertConfig, delay = 5000) {
            this.hideAlert();
            this.localAlertConfig = { ...config };
            this.alertTimeout = setTimeout(() => {
                this.hideAlert();
            }, delay);
        }

        private goToCriterion(riskProfileId: string, coverageType: TprmRequirementType): void {
            this.$emit('goToCriterion', riskProfileId, coverageType);
        }

        private finishGrantExceptionProcedure(modified: boolean, entityName: string): void {
            if (!modified) {
                return;
            }
            if (this.grantExceptionStatus === OperationStatus.success) {
                this.setTimedAlert({
                    icon: this.faCheckCircle,
                    type: 'success',
                    title: `Exception granted for ${entityName}`,
                });
                this.$emit('evaluationUpdated');
            } else {
                this.setTimedAlert({
                    icon: this.faExclamationTriangle,
                    type: 'danger',
                    title: `Failed to grant exception for ${entityName}`,
                });
            }
        }

        private grantExceptions(exceptions: ExceptionInput[], requirement?: EntityRequirement | null) {
            /*
             * we could have no coverage passed for 2 cases,
             * entity level or entity exception (collateral) from collateral tab
             * while exceptions can be array, at the moment the only time there will be more than one is when grant all
             * for all non-compliant criteria. in this case all exception are associated with one single coverage type
             * hence it's safe to grab coverage type from the first index of exceptions.
             * */
            if (exceptions.length === 0) {
                this.setTimedAlert({
                    icon: this.faExclamationTriangle,
                    type: 'danger',
                    title: 'No non-compliant criteria available to grant an exception for',
                });
                return;
            }
            let associatedCoverage = requirement;
            if (!associatedCoverage && exceptions[0].coverageType) {
                associatedCoverage =
                    this.requirementDetailsList.find(
                        (detail) => detail.coverageType === exceptions[0].coverageType,
                    )?.coverage;
            }
            this.$procedures.execute('grantExceptionProcedure', {
                exception: {
                    rpName: this.rpName,
                    insured: this.entity,
                    data: exceptions,
                },
                requirement: associatedCoverage,
            }, (modified: boolean, entityName: string) =>
                this.finishGrantExceptionProcedure(modified, entityName));
        }

        private finishRemoveExceptionProcedure(modified: boolean, entityName: string): void {
            if (!modified) {
                return;
            }
            if (this.removeExceptionStatus === OperationStatus.success) {
                this.setTimedAlert({
                    icon: this.faCheckCircle,
                    type: 'success',
                    title: `Exception removed for ${entityName}`,
                });
                this.$emit('evaluationUpdated');
            } else {
                this.setTimedAlert({
                    icon: this.faExclamationTriangle,
                    type: 'danger',
                    title: `Failed to remove exception for ${entityName}`,
                });
            }
        }

        private removeExceptions(exceptionIds: string[]): void {
            this.$procedures.execute('removeExceptionProcedure', {
                rpName: this.rpName,
                entity: this.entity,
                exceptionIds,
            }, (modified: boolean, entityName: string) =>
                this.finishRemoveExceptionProcedure(modified, entityName));
        }

        private openForceSubmissionModal(request: ForceSubmissionRequest): void {
            this.$procedures.execute('forceSubmissionProcedure', {
                rpName: this.rpName,
                entity: this.entity,
                ...request,
            }, (modified: boolean) => this.finishForceSubmissionProcedure(modified));
        }

        private finishForceSubmissionProcedure(modified: boolean): void {
            if (!modified) {
                return;
            }
            this.$emit('evaluationUpdated');
            useEntityEventsStore().reloadActivityHistory({ rpName: this.rpName, entityId: this.entity.id });
        }

        private openCollateralMappingModal(): void {
            this.$procedures.execute('collateralMappingProcedure', {
                collaterals: this.collateralEntities,
                entityContactEmail: this.entity.contactEmail,
                entityId: this.entity.id,
            });
        }

        private openHistoricDocumentModal(): void {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    tab: ENTITY_DETAILS_TABS.EVENTS,
                    activityType: SUBMISSION_HISTORY_ACTIVITY_TYPES.join(','),
                },
            }).catch(noop);
        }

        private navigateToRiskProfileList(): void {
            this.$router.push({
                name: 'decisioning',
                params: {
                    rpId: this.rpName,
                },
            }).catch(noop);
        }

        private navigateToRiskProfile(id: string): void {
            const route = this.$router.resolve({
                name: 'decisioningRiskProfile',
                params: {
                    rpId: this.rpName,
                    id,
                },
            });
            window.open(route.href, '_blank');
        }

        private getUniqueRequirementDetailsId(requirement: EntityRequirementDetails): string {
            return requirement.instanceId
                ? `${requirement.coverageType} - ${requirement.instanceId}`
                : requirement.coverageType;
        }

        private getEvaErrorMapForRequirement(
            details: EntityRequirementDetails,
        ): Record<string, FieldEvaluationResultError> {
            const key = getEvaluationErrorsMapKey(details.coverageType, details.instanceId);
            return this.allRequirementTypeEvaluationErrors[key] || {};
        }

        private unmounted() {
            clearTimeout(this.alertTimeout);
            this.unsubscribeFormValidity?.();
        }
    }
</script>
